/**
 * Validacion del codigo de sucursal (dSucEm) segun la estructura del CUFE.
 *
 * dSucEm compone el CUFE (ficha tecnica DGI v1.00, Tabla 14: "Codigo de la
 * sucursal B303 dSucEm 4") -- debe tener EXACTAMENTE 4 caracteres. Un codigo
 * mas corto (ej. sucursales guardadas con "1", "02", "009") produce un CUFE
 * mas corto de lo esperado y DGI lo rechaza con un error de longitud
 * (reportado sobre el campo dSucEm, aunque la longitud invalida es la del
 * CUFE completo resultante).
 */

const EXPECTED_LENGTH = 4;

/** true si el codigo tiene exactamente 4 caracteres (formato valido para dSucEm). */
export function isValidBranchOfficeCode(code: string | undefined | null): boolean {
  return !!code && code.length === EXPECTED_LENGTH;
}

export interface CorrectionBranchOfficeCodeResult {
  /** Codigo de 4 caracteres a usar, o null si la correccion debe bloquearse. */
  code: string | null;
  /** true si no se debe permitir continuar con la correccion "mismo CUFE". */
  blocked: boolean;
  /** Mensaje para mostrar al usuario cuando blocked=true. */
  reason?: string;
}

const MISMATCH_REASON =
  'No se puede cambiar la sucursal en una correccion de mismo CUFE porque forma parte del CUFE original. Si necesitas usar otra sucursal, usa "Hacer correccion (nuevo CUFE)".';

const NO_SAFE_PADDING_REASON =
  'El codigo de esta sucursal no puede ajustarse a 4 caracteres. Corrige el codigo de la sucursal o usa "Hacer correccion (nuevo CUFE)".';

/**
 * Resuelve el dSucEm a usar en una correccion "mismo CUFE" cuando el codigo
 * original de la sucursal (el que ya quedo embebido en el CUFE de la
 * factura fallida) no tenia 4 caracteres validos.
 *
 * Regla: dSucEm NO se puede cambiar en una correccion "mismo CUFE" -- pero
 * si el identificador real de la sucursal no cambio (solo le faltaban ceros
 * a la izquierda), rellenar con padStart(4,'0') es una correccion de
 * FORMATO, no un cambio de sucursal, y si es seguro.
 *
 * La comparacion ignora ceros a la izquierda en ambos lados: "7" y "0007"
 * se consideran la misma sucursal; "1" y "0002" no.
 */
export function resolveCorrectionBranchOfficeCode(
  originalCode: string | undefined | null,
  currentCode: string | undefined | null
): CorrectionBranchOfficeCodeResult {
  const original = (originalCode || '').trim();

  if (isValidBranchOfficeCode(original)) {
    // La factura original ya tenia un codigo valido -- nada que rellenar,
    // se preserva tal cual (comportamiento sin cambios).
    return { code: original, blocked: false };
  }

  const current = (currentCode || '').trim();
  if (!current) {
    return { code: null, blocked: true, reason: MISMATCH_REASON };
  }

  const strippedOriginal = original.replace(/^0+/, '');
  const strippedCurrent = current.replace(/^0+/, '');

  if (strippedOriginal !== strippedCurrent) {
    return { code: null, blocked: true, reason: MISMATCH_REASON };
  }

  const padded = current.padStart(EXPECTED_LENGTH, '0');

  if (!isValidBranchOfficeCode(padded)) {
    // El codigo actual tampoco cabe en 4 caracteres (ej. "Test1", 5
    // caracteres) -- no hay relleno seguro posible, sin importar que sea
    // "la misma" sucursal.
    return { code: null, blocked: true, reason: NO_SAFE_PADDING_REASON };
  }

  return { code: padded, blocked: false };
}
